import React, { useState } from 'react';
import { X, Target } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useFinanceStore } from '../store/useFinanceStore';
import { createGoalWithTransaction } from '../services/goalService';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'react-hot-toast';

type ThemeVariant = 'light' | 'dark';

const themeTokens = {
  light: {
    backdrop: 'bg-black/40',
    container: 'bg-white text-slate-900 border border-teal-100 shadow-2xl',
    containerText: 'text-slate-900',
    headerAccentBg: 'bg-emerald-100',
    headerAccentIcon: 'text-emerald-600',
    label: 'text-slate-600',
    helper: 'text-slate-500',
    input: 'bg-white border border-teal-200 placeholder:text-slate-400 focus:border-teal-500 focus:ring-teal-500/20 text-slate-800',
    cancelButton: 'bg-white text-slate-700 border border-teal-100 hover:bg-teal-50',
    mutedIcon: 'text-teal-500',
  },
  dark: {
    backdrop: 'bg-black/60',
    container: 'bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-white border border-slate-700/60',
    containerText: 'text-white',
    headerAccentBg: 'bg-emerald-500/20',
    headerAccentIcon: 'text-emerald-300',
    label: 'text-slate-300',
    helper: 'text-slate-400',
    input: 'bg-slate-900/40 border border-slate-700 placeholder:text-slate-500 focus:border-emerald-500 focus:ring-emerald-500/20 text-slate-100',
    cancelButton: 'bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700',
    mutedIcon: 'text-slate-400',
  },
} as const satisfies Record<ThemeVariant, Record<string, string>>;

interface NewGoalModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NewGoalModal: React.FC<NewGoalModalProps> = ({ isOpen, onClose }) => {
  const { theme } = useTheme();
  const mode: ThemeVariant = theme === 'dark' ? 'dark' : 'light';
  const tokens = themeTokens[mode];
  const { addGoal, addTransaction } = useFinanceStore();
  const [formData, setFormData] = useState({
    name: '',
    targetAmount: '',
    currentAmount: '',
    deadline: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.targetAmount || !formData.deadline) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setIsLoading(true);
    try {
      const goalData = {
        name: formData.name,
        targetAmount: parseFloat(formData.targetAmount),
        currentAmount: parseFloat(formData.currentAmount) || 0,
        deadline: formData.deadline,
        recurring: false
      };

      // Usa o serviço para criar a meta e registrar transação
      const result = await createGoalWithTransaction(
        goalData,
        addGoal,
        addTransaction
      );

      if (result.success) {
        toast.success(result.message);
        
        // Reset form
        setFormData({
          name: '',
          targetAmount: '',
          currentAmount: '',
          deadline: ''
        });
        
        onClose();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Erro ao criar meta:', error);
      toast.error('Erro ao criar meta');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    
    setFormData({
      name: '',
      targetAmount: '',
      currentAmount: '',
      deadline: ''
    });
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className={`absolute inset-0 backdrop-blur-sm ${tokens.backdrop}`}
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.2 }}
            className={`relative rounded-2xl p-6 w-full max-w-md mx-4 overflow-hidden border transition-colors duration-300 ${tokens.container}`}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${tokens.headerAccentBg}`}>
                  <Target className={tokens.headerAccentIcon} size={20} />
                </div>
                <h2 className={`text-xl font-semibold ${tokens.containerText}`}>Nova Meta Financeira</h2>
              </div>
              <button
                onClick={handleClose}
                disabled={isLoading}
                className={`p-2 rounded-lg transition-all duration-200 disabled:opacity-50 ${tokens.label}`}
              >
                <X size={20} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${tokens.label}`}>
                  Título da Meta
                </label>
                <div className="relative">
                  <Target className={`absolute left-3 top-1/2 -translate-y-1/2 ${tokens.mutedIcon}`} size={18} />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ex: Viagem para Europa"
                    disabled={isLoading}
                    className={`w-full pl-10 pr-4 py-3 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed ${tokens.input}`}
                    required
                  />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${tokens.label}`}>
                  Valor da Meta
                </label>
                <div className="relative">
                  <span className={`absolute left-3 top-1/2 -translate-y-1/2 ${tokens.mutedIcon}`}>R$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.targetAmount}
                    onChange={(e) => setFormData(prev => ({ ...prev, targetAmount: e.target.value }))}
                    placeholder="0,00"
                    disabled={isLoading}
                    className={`w-full pl-10 pr-4 py-3 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed ${tokens.input}`}
                    required
                  />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${tokens.label}`}>
                  Valor Guardado (opcional)
                </label>
                <div className="relative">
                  <span className={`absolute left-3 top-1/2 -translate-y-1/2 ${tokens.mutedIcon}`}>R$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.currentAmount}
                    onChange={(e) => setFormData(prev => ({ ...prev, currentAmount: e.target.value }))}
                    placeholder="0"
                    disabled={isLoading}
                    className={`w-full pl-10 pr-4 py-3 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed ${tokens.input}`}
                  />
                </div>
                <p className={`text-xs mt-1 ${tokens.helper}`}>
                  Valor já separado para esta meta (será registrado como despesa)
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${tokens.label}`}>
                  Data Limite
                </label>
                <input
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => setFormData(prev => ({ ...prev, deadline: e.target.value }))}
                  disabled={isLoading}
                  className={`w-full px-4 py-3 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed ${tokens.input}`}
                  required
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isLoading}
                  className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${tokens.cancelButton}`}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white font-medium rounded-lg transition-all duration-200 shadow-lg hover:shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Criando...' : 'Criar Meta'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default NewGoalModal;